import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useMutation } from "@tanstack/react-query";
import { empresasService } from "app/services";
import { useBeforeUnload, useNavigate } from "react-router-dom";
import { nanoid } from "nanoid";
import { toast } from "react-hot-toast";
import { useEmpresasContext } from "../../empresas-context";

const InscripcionContext = createContext();

const initialState = {
  denominacion_social_id: 0,
  empresa_nombre_comercial: "",
  empresa_rtn: "",
  representacion_geografica_id: 0,
  empresa_cantidad_de_empleados: 0,
  camara_de_comercio_id: 0,
  decreto_legislativo_id: 0,
  registro_mercantil_ciudad_id: 0,
  registro_mercantil_fecha: "",
  registro_mercantil_matricula: "",
  registro_mercantil_inscripcion: "",
  registro_mercantil_tomo: "",
  registro_mercantil_capital_autorizado: 0,
  empresa_afiliada: false,
  forma_de_pago_id: 1,
  frecuencia_de_pago_id: 1,
  establecimientos: [],
  telefonos: [],
  celulares: [],
  correos_electronicos: [],
  paginas_web: [],
  encargados: [],
  actividades_economicas: [],
  importaciones: [],
  exportaciones: [],
};

export function InscripcionProvider({ children }) {
  const navigate = useNavigate();

  const [empresa, setEmpresa] = useState(initialState);

  const empresas = useEmpresasContext();

  // save it off before users navigate away
  useBeforeUnload(
    useCallback(() => {
      window.localStorage.setItem("empresa", JSON.stringify(empresa));
    }, [empresa])
  );

  // read it in when they return
  useEffect(() => {
    const value = window.localStorage.getItem("empresa");

    if (!!value) {
      setEmpresa(JSON.parse(value));
    }
  }, []);

  // mutations
  const inscripcionAddMutation = useMutation({
    mutationKey: ["empresas:add:inscripcion"],
    mutationFn: ({ archivos }) => {
      const {
        establecimientos,
        telefonos,
        celulares,
        correos_electronicos,
        paginas_web,
        encargados,
        actividades_economicas,
        importaciones,
        exportaciones,
        ...rest
      } = empresa;

      return toast.promise(
        empresasService.addEmpresa({
          empresa: {
            establecimientos: establecimientos.map(
              ({ establecimiento_id, ...establecimiento }) => establecimiento
            ),
            telefonos: telefonos.map(
              ({ telefono_id, ...telefono }) => telefono
            ),
            celulares: celulares.map(({ celular_id, ...celular }) => celular),
            correos_electronicos: correos_electronicos.map(
              ({ correo_electronico_id, ...correo_electronico }) =>
                correo_electronico
            ),
            paginas_web: paginas_web.map(
              ({ pagina_web_id, ...pagina_web }) => pagina_web
            ),
            encargados: encargados.map(
              ({ encargado_id, ...encargado }) => encargado
            ),
            actividades_economicas: actividades_economicas.map(
              ({ actividad_id, ...actividad_economica }) => actividad_economica
            ),
            importaciones: importaciones.map(
              ({ importacion_id, ...importacion }) => importacion
            ),
            exportaciones: exportaciones.map(
              ({ exportacion_id, ...exportacion }) => exportacion
            ),
            ...rest,
          },
          archivos,
        }),
        {
          loading: "Inscribiendo empresa...",
          success: (response) => {
            return response.statusText;
          },
          error: (error) => error.response.statusText,
        }
      );
    },
    onSuccess: async (response) => {
      const empresa_id = response.headers["empresa_id"];

      const inscripcionBlob = new Blob([response.data], {
        type: "application/pdf",
      });

      const response2 = await empresasService.getDiplomaInscripcion({
        empresaId: empresa_id,
      });

      const diplomaBlob = new Blob([response2.data], {
        type: "application/pdf",
      });

      window.open(window.URL.createObjectURL(diplomaBlob));
      window.open(window.URL.createObjectURL(inscripcionBlob));

      window.localStorage.removeItem("empresa");

      return navigate(`../${empresa_id}`);
    },
  });

  // establecimientos
  function addEstablecimiento({ establecimiento }) {
    const isAlready = empresa.establecimientos.find(
      (est) =>
        est.ciudad_id === establecimiento.ciudad_id &&
        String(est.establecimiento_direccion_detallada) ===
          String(establecimiento.establecimiento_direccion_detallada)
    );

    if (!!isAlready) {
      throw new Error(
        "¡El establecimiento ya está agregado, por favor, intente con otro!"
      );
    }

    setEmpresa((empresa) => ({
      ...empresa,
      establecimientos: [
        ...empresa.establecimientos,
        {
          establecimiento_id: nanoid(),
          ...establecimiento,
        },
      ],
    }));

    return true;
  }

  function updateEstablecimiento({ establecimiento }) {
    const isAlready = empresa.establecimientos.find(
      (est) =>
        est.establecimiento_id !== establecimiento.establecimiento_id &&
        est.ciudad_id === establecimiento.ciudad_id &&
        String(est.establecimiento_direccion_detallada) ===
          String(establecimiento.establecimiento_direccion_detallada)
    );

    if (!!isAlready) {
      throw new Error(
        "¡El establecimiento ya está agregado, por favor, intente con otro!"
      );
    }

    const updated = window.confirm("¿Desea actualizar este establecimiento?");

    if (updated) {
      setEmpresa((empresa) => ({
        ...empresa,
        establecimientos: empresa.establecimientos.map((est) =>
          est.establecimiento_id === establecimiento.establecimiento_id
            ? establecimiento
            : est
        ),
      }));

      return true;
    }

    return false;
  }

  function deleteEstablecimiento({ establecimiento_id }) {
    const deleted = window.confirm("¿Desea eliminar este establecimiento?");

    if (deleted) {
      setEmpresa((empresa) => ({
        ...empresa,
        establecimientos: empresa.establecimientos.filter(
          (est) => est.establecimiento_id !== establecimiento_id
        ),
      }));

      return true;
    }

    return false;
  }

  // números de teléfono
  function addTelefono({ telefono }) {
    const isAlready = empresa.telefonos.find(
      (tel) =>
        String(tel.telefono_numero).replace("-", "") ===
        String(telefono.telefono_numero).replace("-", "")
    );

    if (!!isAlready) {
      throw new Error(
        "¡El número de teléfono ya está agregado, por favor, intente con otro!"
      );
    }

    setEmpresa((empresa) => ({
      ...empresa,
      telefonos: [
        ...empresa.telefonos,
        {
          telefono_id: nanoid(),
          ...telefono,
        },
      ],
    }));

    return true;
  }

  function updateTelefono({ telefono }) {
    const isAlready = empresa.telefonos.find(
      (tel) =>
        tel.telefono_id !== telefono.telefono_id &&
        String(tel.telefono_numero).replace("-", "") ===
          String(telefono.telefono_numero).replace("-", "")
    );

    if (!!isAlready) {
      throw new Error(
        "¡El número de teléfono ya está agregado, por favor, intente con otro!"
      );
    }

    const updated = window.confirm(
      "¿Desea actualizar este número de teléfono?"
    );

    if (updated) {
      setEmpresa((empresa) => ({
        ...empresa,
        telefonos: empresa.telefonos.map((tel) =>
          tel.telefono_id === telefono.telefono_id ? telefono : tel
        ),
      }));

      return true;
    }

    return false;
  }

  function deleteTelefono({ telefono_id }) {
    const deleted = window.confirm("¿Desea eliminar este número de teléfono?");

    if (deleted) {
      setEmpresa((empresa) => ({
        ...empresa,
        telefonos: empresa.telefonos.filter(
          (tel) => tel.telefono_id !== telefono_id
        ),
      }));

      return true;
    }

    return false;
  }

  // números de celular
  function addCelular({ celular }) {
    const isAlready = empresa.celulares.find(
      (cel) =>
        String(cel.celular_numero).replace("-", "") ===
        String(celular.celular_numero).replace("-", "")
    );

    if (!!isAlready) {
      throw new Error(
        "¡El número de celular ya está agregado, por favor, intente con otro!"
      );
    }

    setEmpresa((empresa) => ({
      ...empresa,
      celulares: [
        ...empresa.celulares,
        {
          celular_id: nanoid(),
          ...celular,
        },
      ],
    }));

    return true;
  }

  function updateCelular({ celular }) {
    const isAlready = empresa.celulares.find(
      (cel) =>
        cel.celular_id !== celular.celular_id &&
        String(cel.celular_numero).replace("-", "") ===
          String(celular.celular_numero).replace("-", "")
    );

    if (!!isAlready) {
      throw new Error(
        "¡El número de celular ya está agregado, por favor, intente con otro!"
      );
    }

    const updated = window.confirm("¿Desea actualizar este número de celular?");

    if (updated) {
      setEmpresa((empresa) => ({
        ...empresa,
        celulares: empresa.celulares.map((cel) =>
          cel.celular_id === celular.celular_id ? celular : cel
        ),
      }));

      return true;
    }

    return false;
  }

  function deleteCelular({ celular_id }) {
    const deleted = window.confirm("¿Desea eliminar este número de celular?");

    if (deleted) {
      setEmpresa((empresa) => ({
        ...empresa,
        celulares: empresa.celulares.filter(
          (cel) => cel.celular_id !== celular_id
        ),
      }));

      return true;
    }

    return false;
  }

  // direcciones de correo electrónico
  function addCorreoElectronico({ correo_electronico }) {
    const isAlready = empresa.correos_electronicos.find(
      (cor) =>
        String(cor.correo_electronico_direccion) ===
        String(correo_electronico.correo_electronico_direccion)
    );

    if (!!isAlready) {
      throw new Error(
        "¡La dirección de correo electrónico ya está agregada, por favor, intente con otra!"
      );
    }

    setEmpresa((empresa) => ({
      ...empresa,
      correos_electronicos: [
        ...empresa.correos_electronicos,
        {
          correo_electronico_id: nanoid(),
          ...correo_electronico,
        },
      ],
    }));

    return true;
  }

  function updateCorreoElectronico({ correo_electronico }) {
    const isAlready = empresa.correos_electronicos.find(
      (cor) =>
        cor.correo_electronico_id !==
          correo_electronico.correo_electronico_id &&
        String(cor.correo_electronico_direccion) ===
          String(correo_electronico.correo_electronico_direccion)
    );

    if (!!isAlready) {
      throw new Error(
        "¡La dirección de correo electrónico ya está agregada, por favor, intente con otra!"
      );
    }

    const updated = window.confirm(
      "¿Desea actualizar esta dirección de correo electrónico?"
    );

    if (updated) {
      setEmpresa((empresa) => ({
        ...empresa,
        correos_electronicos: empresa.correos_electronicos.map((cor) => {
          if (
            cor.correo_electronico_id ===
            correo_electronico.correo_electronico_id
          ) {
            return correo_electronico;
          }

          return cor;
        }),
      }));

      return true;
    }

    return false;
  }

  function deleteCorreoElectronico({ correo_electronico_id }) {
    const deleted = window.confirm(
      "¿Desea eliminar esta dirección de correo electrónico?"
    );

    if (deleted) {
      setEmpresa((empresa) => ({
        ...empresa,
        correos_electronicos: empresa.correos_electronicos.filter(
          (cor) => cor.correo_electronico_id !== correo_electronico_id
        ),
      }));

      return true;
    }

    return false;
  }

  // páginas web
  function addPaginaWeb({ pagina_web }) {
    const isAlready = empresa.paginas_web.find(
      (pag) => String(pag.pagina_web_url) === String(pagina_web.pagina_web_url)
    );

    if (!!isAlready) {
      throw new Error(
        "¡La dirección de página web ya está agregada, por favor, intente con otra!"
      );
    }

    setEmpresa((empresa) => ({
      ...empresa,
      paginas_web: [
        ...empresa.paginas_web,
        {
          pagina_web_id: nanoid(),
          ...pagina_web,
        },
      ],
    }));

    return true;
  }

  function updatePaginaWeb({ pagina_web }) {
    const isAlready = empresa.paginas_web.find(
      (pag) =>
        pag.pagina_web_id !== pagina_web.pagina_web_id &&
        String(pag.pagina_web_url) === String(pagina_web.pagina_web_url)
    );

    if (!!isAlready) {
      throw new Error(
        "¡La dirección de página web ya está agregada, por favor, intente con otra!"
      );
    }

    const updated = window.confirm(
      "¿Desea actualizar esta dirección de página web?"
    );

    if (updated) {
      setEmpresa((empresa) => ({
        ...empresa,
        paginas_web: empresa.paginas_web.map((pag) =>
          pag.pagina_web_id === pagina_web.pagina_web_id ? pagina_web : pag
        ),
      }));

      return true;
    }

    return false;
  }

  function deletePaginaWeb({ pagina_web_id }) {
    const deleted = window.confirm(
      "¿Desea eliminar esta dirección de página web?"
    );

    if (deleted) {
      setEmpresa((empresa) => ({
        ...empresa,
        paginas_web: empresa.paginas_web.filter(
          (pag) => pag.pagina_web_id !== pagina_web_id
        ),
      }));

      return true;
    }

    return false;
  }

  // encargados
  function addEncargado({ encargado }) {
    const identificacionIsAlready = empresa.encargados.some(
      (enc) =>
        String(enc.encargado_identificacion) ===
        String(encargado.encargado_identificacion)
    );

    if (!!identificacionIsAlready) {
      throw new Error(
        `¡Ya está agregado un encargado con esta identificación: ${encargado.encargado_identificacion}, por favor, pruebe con otra identificación!`
      );
    }

    const rtnIsAlready = empresa.encargados.some(
      (enc) => String(enc.encargado_rtn) === String(encargado.encargado_rtn)
    );

    if (!!rtnIsAlready) {
      throw new Error(
        `¡Ya está agregado un encargado con este número de RTN: ${encargado.encargado_rtn}, por favor, pruebe con otro número de RTN!`
      );
    }

    const celularIsAlready = empresa.encargados.some(
      (enc) =>
        String(enc.encargado_celular).replace("-", "") ===
        String(encargado.encargado_celular).replace("-", "")
    );

    if (!!celularIsAlready) {
      throw new Error(
        `¡Ya está agregado un encargado con este número de celular: ${encargado.encargado_celular}, por favor, pruebe con otro número de celular!`
      );
    }

    setEmpresa((empresa) => ({
      ...empresa,
      encargados: [
        ...empresa.encargados,
        {
          encargado_id: nanoid(),
          ...encargado,
        },
      ],
    }));

    return true;
  }

  function updateEncargado({ encargado }) {
    const identificacionIsAlready = empresa.encargados.some(
      (enc) =>
        enc.encargado_id !== encargado.encargado_id &&
        String(enc.encargado_identificacion) ===
          String(encargado.encargado_identificacion)
    );

    if (!!identificacionIsAlready) {
      throw new Error(
        `¡Ya está agregado un encargado con esta identificación: ${encargado.encargado_identificacion}, por favor, pruebe con otra identificación!`
      );
    }

    const rtnIsAlready = empresa.encargados.some(
      (enc) =>
        enc.encargado_id !== encargado.encargado_id &&
        String(enc.encargado_rtn) === String(encargado.encargado_rtn)
    );

    if (!!rtnIsAlready) {
      throw new Error(
        `¡Ya está agregado un encargado con este número de RTN: ${encargado.encargado_rtn}, por favor, pruebe con otro número de RTN!`
      );
    }

    const celularIsAlready = empresa.encargados.some(
      (enc) =>
        enc.encargado_id !== encargado.encargado_id &&
        String(enc.encargado_celular).replace("-", "") ===
          String(encargado.encargado_celular).replace("-", "")
    );

    if (!!celularIsAlready) {
      throw new Error(
        `¡Ya está agregado un encargado con este número de celular: ${encargado.encargado_celular}, por favor, pruebe con otro número de celular!`
      );
    }

    const updated = window.confirm("¿Desea actualizar este encargado?");

    if (updated) {
      setEmpresa((empresa) => ({
        ...empresa,
        encargados: empresa.encargados.map((enc) =>
          enc.encargado_id === encargado.encargado_id ? encargado : enc
        ),
      }));

      return true;
    }

    return false;
  }

  function deleteEncargado({ encargado_id }) {
    const deleted = window.confirm("¿Desea eliminar este encargado?");

    if (deleted) {
      setEmpresa((empresa) => ({
        ...empresa,
        encargados: empresa.encargados.filter(
          (enc) => enc.encargado_id !== encargado_id
        ),
      }));

      return true;
    }

    return false;
  }

  // actividades económicas
  function addActividadEconomica({ actividad_economica }) {
    const actividadEconomicaIsAlready = empresa.actividades_economicas.some(
      (act) =>
        act.actividad_economica_id ===
        actividad_economica.actividad_economica_id
    );

    if (!!actividadEconomicaIsAlready) {
      throw new Error(
        "¡La actividad económica ya está agregada, por favor, intente con otra actividad económica!"
      );
    }

    setEmpresa((empresa) => ({
      ...empresa,
      actividades_economicas: [
        ...empresa.actividades_economicas,
        {
          actividad_id: nanoid(),
          ...actividad_economica,
        },
      ],
    }));

    return true;
  }

  function updateActividadEconomica({ actividad_economica }) {
    const actividadEconomicaIsAlready = empresa.actividades_economicas.some(
      (act) =>
        act.actividad_id !== actividad_economica.actividad_id &&
        act.actividad_economica_id ===
          actividad_economica.actividad_economica_id
    );

    if (!!actividadEconomicaIsAlready) {
      throw new Error(
        "¡La actividad económica ya está agregada, por favor, intente con otra actividad económica!"
      );
    }

    const updated = window.confirm(
      "¿Desea actualizar esta actividad económica?"
    );

    if (updated) {
      setEmpresa((empresa) => ({
        ...empresa,
        actividades_economicas: empresa.actividades_economicas.map((act) =>
          act.actividad_id === actividad_economica.actividad_id
            ? actividad_economica
            : act
        ),
      }));

      return true;
    }

    return false;
  }

  function deleteActividadEconomica({ actividad_id }) {
    const deleted = window.confirm("¿Desea eliminar esta actividad económica?");

    if (deleted) {
      setEmpresa((empresa) => ({
        ...empresa,
        actividades_economicas: empresa.actividades_economicas.filter(
          (act) => act.actividad_id !== actividad_id
        ),
      }));

      return true;
    }

    return false;
  }

  // importaciones
  function addImportacion({ importacion }) {
    const importacionIsAlready = empresa.importaciones.some(
      (imp) =>
        String(imp.importacion_detalles).toLowerCase() ===
        String(importacion.importacion_detalles).toLowerCase()
    );

    if (!!importacionIsAlready) {
      throw new Error(
        "¡La importación ya está agregada, por favor, intente con otra importación!"
      );
    }

    setEmpresa((empresa) => ({
      ...empresa,
      importaciones: [
        ...empresa.importaciones,
        {
          importacion_id: nanoid(),
          ...importacion,
        },
      ],
    }));

    return true;
  }

  function updateImportacion({ importacion }) {
    const importacionIsAlready = empresa.importaciones.some(
      (imp) =>
        imp.importacion_id !== importacion.importacion_id &&
        String(imp.importacion_detalles).toLowerCase() ===
          String(importacion.importacion_detalles).toLowerCase()
    );

    if (!!importacionIsAlready) {
      throw new Error(
        "¡La importación ya está agregada, por favor, intente con otra importación!"
      );
    }

    const updated = window.confirm("¿Desea actualizar esta importación?");

    if (updated) {
      setEmpresa((empresa) => ({
        ...empresa,
        importaciones: empresa.importaciones.map((imp) =>
          imp.importacion_id === importacion.importacion_id ? importacion : imp
        ),
      }));

      return true;
    }

    return false;
  }

  function deleteImportacion({ importacion_id }) {
    const deleted = window.confirm("¿Desea eliminar esta importación?");

    if (deleted) {
      setEmpresa((empresa) => ({
        ...empresa,
        importaciones: empresa.importaciones.filter(
          (imp) => imp.importacion_id !== importacion_id
        ),
      }));

      return true;
    }

    return false;
  }

  // exportaciones
  function addExportacion({ exportacion }) {
    const exportacionIsAlready = empresa.exportaciones.some(
      (exp) =>
        String(exp.exportacion_detalles).toLowerCase() ===
        String(exportacion.exportacion_detalles).toLowerCase()
    );

    if (!!exportacionIsAlready) {
      throw new Error(
        "¡La exportación ya está agregada, por favor, intente con otra exportación!"
      );
    }

    setEmpresa((empresa) => ({
      ...empresa,
      exportaciones: [
        ...empresa.exportaciones,
        {
          exportacion_id: nanoid(),
          ...exportacion,
        },
      ],
    }));

    return true;
  }

  function updateExportacion({ exportacion }) {
    const exportacionIsAlready = empresa.exportaciones.some(
      (exp) =>
        exp.exportacion_id !== exportacion.exportacion_id &&
        String(exp.exportacion_detalles).toLowerCase() ===
          String(exportacion.exportacion_detalles).toLowerCase()
    );

    if (!!exportacionIsAlready) {
      throw new Error(
        "¡La exportación ya está agregada, por favor, intente con otra exportación!"
      );
    }

    const updated = window.confirm("¿Desea actualizar esta exportación?");

    if (updated) {
      setEmpresa((empresa) => ({
        ...empresa,
        exportaciones: empresa.exportaciones.map((exp) =>
          exp.exportacion_id === exportacion.exportacion_id ? exportacion : exp
        ),
      }));

      return true;
    }

    return false;
  }

  function deleteExportacion({ exportacion_id }) {
    const deleted = window.confirm("¿Desea eliminar esta exportación?");

    if (deleted) {
      setEmpresa((empresa) => ({
        ...empresa,
        exportaciones: empresa.exportaciones.filter(
          (exp) => exp.exportacion_id !== exportacion_id
        ),
      }));

      return true;
    }

    return false;
  }

  // enviar reinscripción
  async function inscribir({ archivos }) {
    const confirmed = window.confirm("¿Desea inscribir esta empresa?");

    if (confirmed) {
      return await inscripcionAddMutation.mutateAsync({ archivos });
    }

    return false;
  }

  function reset() {
    setEmpresa(initialState);
    window.localStorage.removeItem("empresa");
  }

  return (
    <InscripcionContext.Provider
      value={{
        empresa,
        setEmpresa,
        // establecimientos
        addEstablecimiento,
        updateEstablecimiento,
        deleteEstablecimiento,
        // números de teléfono
        addTelefono,
        updateTelefono,
        deleteTelefono,
        // números de celular
        addCelular,
        updateCelular,
        deleteCelular,
        // direcciones de correo electrónico
        addCorreoElectronico,
        updateCorreoElectronico,
        deleteCorreoElectronico,
        // direcciones de página web
        addPaginaWeb,
        updatePaginaWeb,
        deletePaginaWeb,
        // encargados
        addEncargado,
        updateEncargado,
        deleteEncargado,
        //actividades económicas
        addActividadEconomica,
        updateActividadEconomica,
        deleteActividadEconomica,
        // importaciones
        addImportacion,
        updateImportacion,
        deleteImportacion,
        //exportaciones
        addExportacion,
        updateExportacion,
        deleteExportacion,
        //inscribir
        inscribir,
        isSending: inscripcionAddMutation.isLoading,
        // reiniciar
        reset,
      }}
    >
      {children}
    </InscripcionContext.Provider>
  );
}

export function useInscripcionContext() {
  const inscripcionContext = useContext(InscripcionContext);

  if (!inscripcionContext) {
    throw new Error(
      "useInscripcionContext should be used inside the InscripcionProvider."
    );
  }

  return inscripcionContext;
}
